import React, { useState } from "react";
import { Smartphone, X } from "lucide-react";

const TemplatePreviewModal = ({ isOpen, closeModal, previews = {} }) => {
  const [activeTab, setActiveTab] = useState(0);

  // Extract platforms from the preview data
  const availablePlatforms = [];
  if (previews.FCM) availablePlatforms.push({ key: "FCM", label: "Android" });
  if (previews.WEB) availablePlatforms.push({ key: "WEB", label: "Web" });
  if (previews.APNS) availablePlatforms.push({ key: "APNS", label: "iOS" });

  // Default to Android if no data provided
  const platforms =
    availablePlatforms.length > 0
      ? availablePlatforms
      : [{ key: "FCM", label: "Android" }];

  // Helper function to strip HTML tags from body text
  const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "");
  };

  // Helper function to get notification data for a platform
  const getNotificationData = (platformKey) => {
    if (!previews[platformKey]) {
      return {
        title: "Sample Notification",
        body: "This is how your notification will appear",
        actions: [],
      };
    }

    switch (platformKey) {
      case "FCM":
        return {
          title: previews.FCM.notification?.title || "Notification",
          body: stripHtml(previews.FCM.notification?.body || ""),
          actions: previews.FCM.android?.notification?.actions || [],
          deeplink: previews.FCM.data?.deeplink,
        };
      case "WEB":
        return {
          title: previews.WEB.title || "Notification",
          body: stripHtml(previews.WEB.body || ""),
          actions: previews.WEB.actions || [],
          url: previews.WEB.data?.url,
        };
      case "APNS":
        return {
          title: previews.APNS.aps?.alert?.title || "Notification",
          body: stripHtml(previews.APNS.aps?.alert?.body || ""),
          actions: previews.APNS.actions || [],
        };
      default:
        return {
          title: "Sample Notification",
          body: "This is how your notification will appear",
          actions: [],
        };
    }
  };

  const AndroidPreview = ({ notificationData }) => (
    <div className="w-full max-w-sm mx-auto transform scale-90 sm:scale-100">
      {/* Realistic Android Phone Frame */}
      <div className="relative">
        {/* Phone Shadow */}
        <div className="absolute inset-0 bg-black/20 rounded-[2.5rem] blur-xl transform translate-y-2"></div>

        {/* Phone Body */}
        <div className="relative bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-[2.5rem] p-2 shadow-2xl">
          {/* Screen */}
          <div className="bg-black rounded-[2rem] overflow-hidden relative">
            {/* Punch Hole Camera */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-6 h-6 bg-black rounded-full z-20 shadow-inner"></div>

            {/* Screen Content */}
            <div className="relative bg-gradient-to-br from-slate-900 via-gray-900 to-black min-h-[640px]">
              {/* Status Bar */}
              <div className="flex justify-between items-center px-6 pt-12 pb-2 text-white text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">9:41</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {/* Signal Bars */}
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`w-1 rounded-full ${
                          i <= 3 ? "bg-white" : "bg-gray-600"
                        }`}
                        style={{ height: `${i * 2 + 2}px` }}
                      ></div>
                    ))}
                  </div>
                  {/* WiFi */}
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 0 1 .808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 0 1-1.414 1.414zM14.95 11.05a7 7 0 0 0-9.9 0 1 1 0 0 1-1.414-1.414 9 9 0 0 1 12.728 0 1 1 0 0 1-1.414 1.414zM12.12 13.88a3 3 0 0 0-4.24 0 1 1 0 0 1-1.415-1.415 5 5 0 0 1 7.07 0 1 1 0 0 1-1.415 1.415zM9 16a1 1 0 0 1 2 0 1 1 0 0 1-2 0z" />
                  </svg>
                  {/* Battery */}
                  <div className="flex items-center">
                    <div className="w-6 h-3 border border-white rounded-sm relative">
                      <div className="w-4 h-1.5 bg-white rounded-sm m-0.5"></div>
                    </div>
                    <div className="w-0.5 h-1.5 bg-white rounded-r ml-0.5"></div>
                  </div>
                </div>
              </div>

              {/* Wallpaper Area */}
              <div className="px-6 pt-8 pb-24">
                {/* Time Widget */}
                <div className="text-center text-white mb-12">
                  <div className="text-7xl font-thin tracking-tight mb-1">
                    9:41
                  </div>
                  <div className="text-lg text-gray-300">Monday, August 11</div>
                </div>

                {/* Notification Card */}
                <div className="bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-700/50 overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      {/* App Icon */}
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg flex-shrink-0">
                        <svg
                          className="w-6 h-6 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        ></svg>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-white font-semibold text-md tracking-wide">
                            Your App
                          </h3>
                          <span className="text-gray-400 text-sm">now</span>
                        </div>

                        <h4 className="text-white font-medium text-sm mb-2 leading-tight">
                          {notificationData.title}
                        </h4>

                        <p className="text-gray-200 text-sm leading-relaxed">
                          {notificationData.body}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {notificationData.actions &&
                    notificationData.actions.length > 0 && (
                      <div className="flex border-t border-gray-700">
                        {notificationData.actions
                          .slice(0, 2)
                          .map((action, index) => (
                            <button
                              key={index}
                              className="flex-1 py-4 text-blue-400 font-medium text-base hover:bg-gray-700/50 transition-colors"
                            >
                              {action.title ||
                                action.label ||
                                `Action ${index + 1}`}
                            </button>
                          ))}
                      </div>
                    )}
                </div>
              </div>

              {/* Navigation Bar */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-black/80 backdrop-blur-sm flex items-center justify-center">
                <div className="w-32 h-1 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const IOSPreview = ({ notificationData }) => (
    <div className="w-full max-w-sm mx-auto transform scale-90 sm:scale-100">
      {/* Realistic iPhone Frame */}
      <div className="relative">
        {/* Phone Shadow */}
        <div className="absolute inset-0 bg-black/30 rounded-[2.5rem] blur-2xl transform translate-y-3"></div>

        {/* Phone Body */}
        <div className="relative bg-gradient-to-br from-gray-800 via-black to-gray-900 rounded-[2.5rem] p-1.5 shadow-2xl">
          {/* Screen */}
          <div className="bg-black rounded-[2.25rem] overflow-hidden relative">
            {/* Dynamic Island */}
            <div className="absolute top-3 left-1/2 transform -translate-x-1/2 w-32 h-7 bg-black rounded-full z-20 shadow-2xl border border-gray-800"></div>

            {/* Screen Content */}
            <div className="relative bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 min-h-[640px]">
              {/* Status Bar */}
              <div className="flex justify-between items-center px-8 pt-14 pb-4 text-white">
                <span className="font-semibold text-lg">9:41</span>
                <div className="flex items-center gap-2">
                  {/* Signal Dots */}
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full ${
                          i <= 3 ? "bg-white" : "bg-gray-500"
                        }`}
                      ></div>
                    ))}
                  </div>
                  {/* Battery */}
                  <div className="flex items-center ml-2">
                    <div className="w-7 h-4 border-2 border-white rounded-sm relative">
                      <div className="w-4 h-2 bg-white rounded-sm absolute top-0.5 left-0.5"></div>
                    </div>
                    <div className="w-1 h-2 bg-white rounded-r ml-0.5"></div>
                  </div>
                </div>
              </div>

              {/* Lock Screen Content */}
              <div className="px-8 pt-12">
                {/* Time Display */}
                <div className="text-center text-white mb-16">
                  <div className="text-8xl font-extralight tracking-tighter mb-2">
                    9:41
                  </div>
                  <div className="text-xl font-light">Monday, August 11</div>
                </div>

                {/* Notification */}
                <div className="bg-white/20 backdrop-blur-2xl rounded-3xl border border-white/30 shadow-2xl overflow-hidden">
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      {/* App Icon */}
                      <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-xl flex-shrink-0">
                        <svg
                          className="w-7 h-7 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        ></svg>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-white font-semibold text-md">
                            Your App
                          </h3>
                          <span className="text-white/70 text-sm">now</span>
                        </div>

                        <h4 className="text-white font-semibold text-sm mb-1 leading-tight">
                          {notificationData.title}
                        </h4>

                        <p className="text-white/95 text-sm leading-relaxed">
                          {notificationData.body}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {notificationData.actions &&
                    notificationData.actions.length > 0 && (
                      <div className="flex border-t border-white/20">
                        {notificationData.actions
                          .slice(0, 2)
                          .map((action, index) => (
                            <button
                              key={index}
                              className="flex-1 py-4 text-white font-semibold text-lg hover:bg-white/10 transition-colors"
                            >
                              {action.title ||
                                action.label ||
                                `Action ${index + 1}`}
                            </button>
                          ))}
                      </div>
                    )}
                </div>
              </div>

              {/* Home Indicator */}
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-36 h-1.5 bg-white rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const WebPreview = ({ notificationData }) => (
    <div className="w-full max-w-md mx-auto">
      {/* Browser Window */}
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Browser Chrome */}
        <div className="bg-gray-50 px-4 py-3 border-b flex items-center gap-3">
          <div className="flex gap-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          <div className="flex-1 bg-white rounded-lg px-4 py-2 text-sm text-gray-600 border">
            🔒 yourapp.com
          </div>
        </div>

        {/* Page Content */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 min-h-64">
          {/* Website Header */}
          <div className="mb-8">
            <div className="w-32 h-8 bg-blue-600 rounded mb-4"></div>
            <div className="space-y-2">
              <div className="w-full h-3 bg-gray-300 rounded"></div>
              <div className="w-3/4 h-3 bg-gray-300 rounded"></div>
            </div>
          </div>

          {/* Notification Toast */}
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 p-4 relative overflow-hidden">
            {/* Notification Icon */}
            <div className="absolute top-2 right-2">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">🔔</span>
              </div>
            </div>

            <div className="pr-8">
              <h4 className="font-semibold text-gray-900 text-lg mb-2">
                {notificationData.title}
              </h4>
              <p className="text-gray-700 text-base leading-relaxed mb-4">
                {notificationData.body}
              </p>

              {/* Action Buttons */}
              {notificationData.actions &&
                notificationData.actions.length > 0 && (
                  <div className="flex gap-3">
                    {notificationData.actions
                      .slice(0, 2)
                      .map((action, index) => (
                        <button
                          key={index}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                        >
                          {action.title ||
                            action.label ||
                            `Action ${index + 1}`}
                        </button>
                      ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gray-50">
          <h2 className="text-2xl font-bold text-gray-900">
            Notification Preview
          </h2>
          <button
            onClick={closeModal}
            className="p-2 hover:bg-gray-200 rounded-xl transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[calc(95vh-100px)]">
          {platforms.length > 0 ? (
            <div className="space-y-8">
              {/* Tabs */}
              {platforms.length > 1 && (
                <div className="flex gap-2 bg-gray-100 rounded-xl p-2">
                  {platforms.map((platform, index) => (
                    <button
                      key={platform.key}
                      onClick={() => setActiveTab(index)}
                      className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
                        activeTab === index
                          ? "bg-white shadow-lg text-gray-900 transform scale-105"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                      }`}
                    >
                      {platform.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Preview Content */}
              <div className="flex justify-center py-8">
                {platforms[activeTab]?.key === "FCM" && (
                  <AndroidPreview
                    notificationData={getNotificationData("FCM")}
                  />
                )}
                {platforms[activeTab]?.key === "WEB" && (
                  <WebPreview notificationData={getNotificationData("WEB")} />
                )}
                {platforms[activeTab]?.key === "APNS" && (
                  <IOSPreview notificationData={getNotificationData("APNS")} />
                )}
              </div>

              {/* Data Summary */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h5 className="font-bold text-lg mb-4 text-gray-900">
                  Notification Data
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const data = getNotificationData(platforms[activeTab]?.key);
                    return (
                      <>
                        <div className="bg-white rounded-lg p-4 border">
                          <div className="text-sm font-medium text-gray-500 mb-1">
                            Title
                          </div>
                          <div className="font-semibold text-gray-900">
                            {data.title}
                          </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border">
                          <div className="text-sm font-medium text-gray-500 mb-1">
                            Body
                          </div>
                          <div className="text-gray-900">{data.body}</div>
                        </div>
                        {data.actions && data.actions.length > 0 && (
                          <div className="bg-white rounded-lg p-4 border">
                            <div className="text-sm font-medium text-gray-500 mb-1">
                              Actions
                            </div>
                            <div className="text-gray-900">
                              {data.actions.length} action(s) configured
                            </div>
                          </div>
                        )}
                        {(data.deeplink || data.url) && (
                          <div className="bg-white rounded-lg p-4 border">
                            <div className="text-sm font-medium text-gray-500 mb-1">
                              {data.deeplink ? "Deep Link" : "URL"}
                            </div>
                            <div className="text-blue-600 font-mono text-sm break-all">
                              {data.deeplink || data.url}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500">
              <Smartphone className="w-20 h-20 mx-auto mb-6 text-gray-400" />
              <p className="text-xl font-semibold mb-2">
                No preview data available
              </p>
              <p>Provide notification data to see realistic device previews</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
